import { hasProviderQrImage } from '@p2p/entities/qrInvoice/providerQrImage';
import { isSberUzbekistan } from '@p2p/entities/qrInvoice/sber';
import { usePaymentTemplate } from '@p2p/entities/templates';
import { TGetOrderResponse, TPageSettingsResponse } from '@p2p/services';
import {
  failStatuses,
  IN_DISPUTE,
  NOT_FOUND_PAYMENT,
  ORDER_STATUS,
  successStatuses,
} from '@p2p/shared/constants';
import { routes } from '@p2p/shared/routing';
import { FC, useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import { PAYMENT_STEPS_IDS, SELECTED_BANK_STORAGE_KEY } from './Payment.constants';

interface ICheckPaymentProps {
  order: TGetOrderResponse;
  pageSettings: TPageSettingsResponse;
}
export const CheckPayment: FC<ICheckPaymentProps> = ({ order, pageSettings }) => {
  const { setStepId } = usePaymentTemplate();
  const navigate = useNavigate();
  const match = useMatch(routes.transfer.template);
  const cancelMatch = useMatch(routes.transferCanceled.template);
  const isMatch = !!match;
  const isCancelMatch = !!cancelMatch;
  const isSuccess = successStatuses.includes(order.status);
  const isFail = failStatuses.includes(order.status);
  const isInDispute = order.status === IN_DISPUTE;
  const isSberUzbekistanOrder = isSberUzbekistan(order);
  const isProviderQrImageOrder = hasProviderQrImage(order);

  useEffect(() => {
    const selectedBankId = localStorage.getItem(SELECTED_BANK_STORAGE_KEY);

    if (!selectedBankId || !isMatch || isSuccess || isFail || isInDispute) {
      return;
    }

    const { orderId, bankId } = JSON.parse(selectedBankId);

    if (orderId && orderId === order.id) {
      navigate(routes.transferBank.path({ id: order.id, bankId: String(bankId) }));
    } else {
      localStorage.removeItem(SELECTED_BANK_STORAGE_KEY);
    }
  }, [navigate, order.id, isMatch, isSuccess, isFail, isInDispute]);

  useEffect(() => {
    if (isMatch || isCancelMatch) {
      return;
    }
    if (isSuccess || isFail || isInDispute) {
      navigate(routes.transfer.path({ id: order.id }));
    }
  }, [isMatch, navigate, order.id, isCancelMatch, isSuccess, isFail, isInDispute]);

  useEffect(() => {
    if (!isMatch) {
      return;
    }

    if (isSuccess) {
      setStepId(PAYMENT_STEPS_IDS.PAYMENT_SUCCESS);
      return;
    }

    if (isFail) {
      setStepId(PAYMENT_STEPS_IDS.PAYMENT_WARNING);
      return;
    }

    if (isInDispute) {
      setStepId(PAYMENT_STEPS_IDS.IN_DISPUTE);
      return;
    }

    // P2P-2739: у Phoneix QR приходит ссылкой (order.invoice_url) — реквизит
    // при этом обычный SBP, поэтому qr_invoice=false и без этой проверки
    // страница уходила на обычный перевод, без QR.
    // P2P-3043: QR-картинка провайдера (Viatrum PAYMENT_LINK) — тоже экран QR.
    if (
      order.requisite.qr_invoice ||
      order.invoice_url ||
      isSberUzbekistanOrder ||
      isProviderQrImageOrder
    ) {
      navigate(routes.transferInvoice.path({ id: order.id }));
      return;
    }

    if (pageSettings.cloudflare) {
      setStepId(PAYMENT_STEPS_IDS.CHECK_USER);
    } else {
      setStepId(PAYMENT_STEPS_IDS.SELECT_BANK);
    }
  }, [
    setStepId,
    pageSettings.cloudflare,
    isMatch,
    isSuccess,
    isFail,
    isInDispute,
    order.requisite.qr_invoice,
    order.invoice_url,
    order.id,
    navigate,
    isSberUzbekistanOrder,
    isProviderQrImageOrder,
  ]);

  useEffect(() => {
    if (order.status === ORDER_STATUS.REQUISITES_NOT_FOUND) {
      navigate(`/${order.id}/${NOT_FOUND_PAYMENT}`);
    }
  }, [navigate, order.id, order.status]);

  return null;
};
